import { Prop, Component, h } from "@stencil/core";
import classNames from "classnames";
import { LinkProps } from "./f-link-model";

/**
 * @name    a-link
 * @type    atom
 *
 * @param   {string} variant              Variant of the link (simple, primary)
 * @param   {string} href                 Maps the href attribute to the link.
 * @param   {boolean} disabled            Disables the link and changes its tag to the span
 * @param   {string} target               Maps the "target" attribute to the link. E.g. to allow opening in a new tab (_blank)
 * @param   {string} additionalProps      Additional properties (use JSON format) allows to map any attribute on the link
 * @description
 * representation of links
 */
@Component({
  tag: "f-link",
  styleUrl: "f-link.scss",
  shadow: true,
})
export class FLink {
  @Prop() variant: LinkProps["variant"];
  @Prop() href: LinkProps["href"] = "#";
  @Prop() disabled: LinkProps["disabled"];
  @Prop() target: LinkProps["target"];
  @Prop() additionalProps: LinkProps["additionalProps"];
  @Prop() withParts: LinkProps["withParts"] = true;
  @Prop() isExternal: LinkProps["isExternal"] = false;
  @Prop() externalLinkHiddenText: LinkProps["externalLinkHiddenText"] =
    "Opens in a new window";

  render() {
    const additionalProps = this.additionalProps
      ? JSON.parse(this.additionalProps)
      : "";
    const variantCssClass = this.variant ? "a-link--" + this.variant : "";
    const cssClasses = classNames(
      {
        "a-link": this.variant || this.disabled,
      },
      variantCssClass,
      {
        "a-link--disabled": this.disabled,
      },
      {
        "a-link--with-parts": this.withParts || this.isExternal,
      }
    );

    if (this.disabled) {
      return (
        <span class={cssClasses} {...additionalProps} data-test="link-disable">
          <slot />
        </span>
      );
    }

    return (
      <a
        class={cssClasses}
        href={this.href}
        target={this.target || (this.isExternal && "_blank") || null}
        {...additionalProps}
        data-test="link"
      >
        <slot />
        {this.isExternal
          ? [
              <f-icon iconName="externallink" />,
              <f-visually-hidden>
                {this.externalLinkHiddenText}
              </f-visually-hidden>,
            ]
          : ""}
      </a>
    );
  }
}
